'use client';

import { c as _c } from "react/compiler-runtime";
import CommentBlock from '@/app/[locale]/(worlds)/[roomId]/components/CommentBlock';
import { ShowMoreReplies, ShowReplyToggle } from '@/app/[locale]/(worlds)/[roomId]/components/CommentReplyButtons';
import { LoadingDots } from '@/components/LoadingDots';
import { Separator } from '@/components/ui/separator';
import { ReportReason } from '@/constants/comment';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { deleteCommentAction, postCommentAction, reportCommentAction, toggleLikeAction } from '@/server/lib/comment';
import { getComments } from '@/services/cms';
import type { Comment, CommentsData } from '@/types/comments';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
const REPLY_PAGE_LIMIT = 10;
const replyLikeChange = (reply: Comment, isLiked: boolean) => {
  const {
    is_liked,
    ...rest
  } = reply;
  return {
    ...rest,
    like_count: Math.max(0, reply.like_count + (isLiked ? 1 : -1)),
    ...(isLiked && {
      is_liked: true
    })
  };
};
interface CommentProps {
  comment: Comment;
  lastCommentRef?: (node: HTMLDivElement | null) => void;
  removeComment: (commentId: string) => void;
  updateCommentReplyCount: (commentId: string, delta: number) => void;
  updateCommentLikeCount: (commentId: string, isLiked: boolean) => void;
  reportComment: (commentId: string, reason: ReportReason, detail: string) => void;
}
function Comment(t0) {
  const $ = _c(89);
  const {
    comment,
    lastCommentRef,
    removeComment,
    updateCommentReplyCount,
    updateCommentLikeCount,
    reportComment
  } = t0;
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    store
  } = useConfigsContext();
  const [showReplies, setShowReplies] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tempReplies, setTempReplies] = useState(t1);
  let t2;
  if ($[1] !== comment.id || $[2] !== comment.reply_count || $[3] !== roomData.id || $[4] !== showReplies || $[5] !== tempReplies.length) {
    t2 = (pageIndex, previousPageData) => {
      if (comment.reply_count === tempReplies.length || !showReplies) {
        return null;
      }
      if (pageIndex === 0 || !previousPageData) {
        return `comments?room_id=${roomData.id}&parent_id=${comment.id}&limit=${REPLY_PAGE_LIMIT}&cursor`;
      } else {
        return previousPageData.pages.next;
      }
    };
    $[1] = comment.id;
    $[2] = comment.reply_count;
    $[3] = roomData.id;
    $[4] = showReplies;
    $[5] = tempReplies.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const getKey = t2;
  let t3;
  if ($[7] !== comment.id || $[8] !== roomData.id || $[9] !== store?.credentials?.htcToken) {
    t3 = key => {
      const urlParams = new URLSearchParams(key.split("?")[1]);
      const cursor = urlParams.get("cursor") || "";
      return getComments({
        roomId: roomData.id,
        parentId: comment.id,
        cursor,
        limit: REPLY_PAGE_LIMIT,
        htcAuthKey: store?.credentials?.htcToken
      });
    };
    $[7] = comment.id;
    $[8] = roomData.id;
    $[9] = store?.credentials?.htcToken;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      revalidateFirstPage: false
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    data,
    isLoading,
    isValidating,
    setSize,
    mutate
  } = useSWRInfinite(getKey, t3, t4);
  let t5;
  if ($[12] !== data) {
    t5 = data && data?.length > 0 ? data.flatMap(_temp) : [];
    $[12] = data;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const baseReplies = t5;
  let t6;
  let t7;
  if ($[14] !== baseReplies) {
    t6 = () => {
      if (baseReplies.length === 0) {
        return;
      }
      const baseIds = new Set(baseReplies.map(_temp2));
      setTempReplies(prev => {
        const hasOverlap = prev.some(reply_0 => baseIds.has(reply_0.id));
        if (hasOverlap) {
          return prev.filter(reply_1 => !baseIds.has(reply_1.id));
        }
        return prev;
      });
    };
    t7 = [baseReplies];
    $[14] = baseReplies;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  if ($[17] !== tempReplies) {
    t8 = new Set(tempReplies.map(_temp3));
    $[17] = tempReplies;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const tempIds = t8;
  let t9;
  if ($[19] !== baseReplies || $[20] !== tempIds || $[21] !== tempReplies) {
    let t10;
    if ($[23] !== tempIds) {
      t10 = reply_3 => !tempIds.has(reply_3.id);
      $[23] = tempIds;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    const filteredBase = baseReplies.filter(t10);
    t9 = [...filteredBase, ...tempReplies];
    $[19] = baseReplies;
    $[20] = tempIds;
    $[21] = tempReplies;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const renderReplies = t9;
  const canLoadMore = !!(!isLoading && !isValidating && data && data.length > 0 && data[data.length - 1]?.pages?.next);
  let t10;
  if ($[25] !== comment.id || $[26] !== roomData.id || $[27] !== store?.credentials?.htcToken || $[28] !== updateCommentReplyCount) {
    t10 = async value => {
      const htcToken = store?.credentials?.htcToken;
      if (!value.trim() || !htcToken || !roomData?.id) {
        return;
      }
      ;
      try {
        const created = await postCommentAction({
          roomId: roomData.id,
          parentId: comment.id,
          content: value,
          htcAuthKey: htcToken
        });
        setTempReplies(prev_0 => [...prev_0, created]);
        updateCommentReplyCount(comment.id, 1);
      } catch (t11) {
        const err = t11;
        console.error("Failed to add Reply:", err);
      }
    };
    $[25] = comment.id;
    $[26] = roomData.id;
    $[27] = store?.credentials?.htcToken;
    $[28] = updateCommentReplyCount;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const addReply = t10;
  let t11;
  if ($[30] !== data || $[31] !== mutate || $[32] !== store?.credentials?.htcToken) {
    t11 = async (replyId, reason, detail) => {
      const htcToken_0 = store?.credentials?.htcToken;
      if (!htcToken_0) {
        return;
      }
      const cache = data;
      ;
      try {
        mutate(pages => pages?.map(page_0 => ({
          ...page_0,
          results: page_0?.results?.map(reply_4 => reply_4.id === replyId ? {
            ...reply_4,
            is_reported: true
          } : reply_4)
        })), false);
        await reportCommentAction({
          commentId: replyId,
          htcAuthKey: htcToken_0,
          reason,
          detail
        });
      } catch (t12) {
        const err_0 = t12;
        mutate(cache, false);
        console.error("Failed to report reply:", err_0);
      }
    };
    $[30] = data;
    $[31] = mutate;
    $[32] = store?.credentials?.htcToken;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const reportReplyMutate = t11;
  let t12;
  if ($[34] !== store?.credentials?.htcToken || $[35] !== tempReplies) {
    t12 = async (replyId_0, isLiked) => {
      const htcToken_1 = store?.credentials?.htcToken;
      if (!htcToken_1) {
        return;
      }
      const cache_0 = tempReplies;
      ;
      try {
        setTempReplies(prev_1 => prev_1.map(reply_5 => reply_5.id === replyId_0 ? replyLikeChange(reply_5, isLiked) : reply_5));
        await toggleLikeAction({
          commentId: replyId_0,
          htcAuthKey: htcToken_1,
          isLiked
        });
      } catch (t13) {
        const err_1 = t13;
        setTempReplies(cache_0);
        throw err_1;
      }
    };
    $[34] = store?.credentials?.htcToken;
    $[35] = tempReplies;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const updateTempReplyLikeCount = t12;
  let t13;
  if ($[37] !== data || $[38] !== mutate || $[39] !== store?.credentials?.htcToken) {
    t13 = async (replyId_1, isLiked_0) => {
      const htcToken_2 = store?.credentials?.htcToken;
      if (!htcToken_2) {
        return;
      }
      const cache_1 = data;
      ;
      try {
        mutate(pages_0 => pages_0?.map(page_1 => ({
          ...page_1,
          results: page_1?.results?.map(reply_6 => reply_6.id === replyId_1 ? replyLikeChange(reply_6, isLiked_0) : reply_6)
        })), false);
        await toggleLikeAction({
          commentId: replyId_1,
          htcAuthKey: htcToken_2,
          isLiked: isLiked_0
        });
      } catch (t14) {
        const err_2 = t14;
        mutate(cache_1, false);
        throw err_2;
      }
    };
    $[37] = data;
    $[38] = mutate;
    $[39] = store?.credentials?.htcToken;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const updateReplyLikeCountMutate = t13;
  let t14;
  if ($[41] !== tempReplies || $[42] !== updateReplyLikeCountMutate || $[43] !== updateTempReplyLikeCount) {
    t14 = async (replyId_2, isLiked_1) => {
      const existedInTemp = tempReplies.some(reply_7 => reply_7.id === replyId_2);
      if (existedInTemp) {
        updateTempReplyLikeCount(replyId_2, isLiked_1);
      } else {
        updateReplyLikeCountMutate(replyId_2, isLiked_1);
      }
    };
    $[41] = tempReplies;
    $[42] = updateReplyLikeCountMutate;
    $[43] = updateTempReplyLikeCount;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const updateReplyLikeCount = t14;
  let t15;
  if ($[45] !== comment.id || $[46] !== store?.credentials?.htcToken || $[47] !== tempReplies || $[48] !== updateCommentReplyCount) {
    t15 = async replyId_3 => {
      const htcToken_3 = store?.credentials?.htcToken;
      if (!htcToken_3) {
        return;
      }
      const cache_2 = tempReplies;
      ;
      try {
        setTempReplies(prev_2 => prev_2.filter(reply_8 => reply_8.id !== replyId_3));
        updateCommentReplyCount(comment.id, -1);
        await deleteCommentAction({
          commentId: replyId_3,
          htcAuthKey: htcToken_3
        });
      } catch (t16) {
        const err_3 = t16;
        setTempReplies(cache_2);
        updateCommentReplyCount(comment.id, 1);
        console.error("Failed to delete temp reply:", err_3);
      }
    };
    $[45] = comment.id;
    $[46] = store?.credentials?.htcToken;
    $[47] = tempReplies;
    $[48] = updateCommentReplyCount;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const removeTempReply = t15;
  let t16;
  if ($[50] !== comment.id || $[51] !== data || $[52] !== mutate || $[53] !== store?.credentials?.htcToken || $[54] !== updateCommentReplyCount) {
    t16 = async replyId_4 => {
      const htcToken_4 = store?.credentials?.htcToken;
      if (!htcToken_4) {
        return;
      }
      const cache_3 = data;
      ;
      try {
        mutate(pages_1 => pages_1?.map(page_2 => ({
          ...page_2,
          results: (page_2?.results || []).filter(reply_9 => reply_9.id !== replyId_4)
        })), false);
        updateCommentReplyCount(comment.id, -1);
        await deleteCommentAction({
          commentId: replyId_4,
          htcAuthKey: htcToken_4
        });
      } catch (t17) {
        const err_4 = t17;
        mutate(cache_3, false);
        updateCommentReplyCount(comment.id, 1);
        console.error("Failed to delete reply:", err_4);
      }
    };
    $[50] = comment.id;
    $[51] = data;
    $[52] = mutate;
    $[53] = store?.credentials?.htcToken;
    $[54] = updateCommentReplyCount;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const removeReplyMutate = t16;
  let t17;
  if ($[56] !== removeReplyMutate || $[57] !== removeTempReply || $[58] !== tempReplies) {
    t17 = async replyId_5 => {
      const existedInTemp_0 = tempReplies.some(reply_10 => reply_10.id === replyId_5);
      if (existedInTemp_0) {
        removeTempReply(replyId_5);
      } else {
        removeReplyMutate(replyId_5);
      }
    };
    $[56] = removeReplyMutate;
    $[57] = removeTempReply;
    $[58] = tempReplies;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  const removeReply = t17;
  let t18;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setShowReplies(_temp4);
    };
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  const toggleReplies = t18;
  let t19;
  if ($[61] !== canLoadMore || $[62] !== setSize) {
    t19 = () => {
      if (canLoadMore) {
        setSize(_temp5);
      }
    };
    $[61] = canLoadMore;
    $[62] = setSize;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  const loadMore = t19;
  let t20;
  let t21;
  if ($[64] !== mutate) {
    t20 = () => () => {
      mutate(undefined, undefined);
    };
    t21 = [mutate];
    $[64] = mutate;
    $[65] = t20;
    $[66] = t21;
  } else {
    t20 = $[65];
    t21 = $[66];
  }
  useEffect(t20, t21);
  let t22;
  if ($[67] !== addReply || $[68] !== comment || $[69] !== removeComment || $[70] !== reportComment || $[71] !== updateCommentLikeCount) {
    t22 = <CommentBlock comment={comment} isReply={false} addComment={addReply} removeComment={removeComment} updateCommentLikeCount={updateCommentLikeCount} reportComment={reportComment} />;
    $[67] = addReply;
    $[68] = comment;
    $[69] = removeComment;
    $[70] = reportComment;
    $[71] = updateCommentLikeCount;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== addReply || $[74] !== canLoadMore || $[75] !== comment.reply_count || $[76] !== isLoading || $[77] !== isValidating || $[78] !== loadMore || $[79] !== removeReply || $[80] !== renderReplies || $[81] !== reportReplyMutate || $[82] !== showReplies || $[83] !== updateReplyLikeCount) {
    t23 = !!comment?.reply_count && <div className="flex flex-col gap-2 pl-10.5 md:pl-16"><ShowReplyToggle toggleReplies={toggleReplies} showReplies={showReplies} replyCount={comment.reply_count} />{showReplies && <><Separator className="bg-vive-surface-normal" /><div className="mt-1 flex flex-col gap-6">{renderReplies.map((reply_11, index) => <CommentBlock key={reply_11.id} comment={reply_11} isReply={true} addComment={addReply} updateCommentLikeCount={updateReplyLikeCount} removeComment={removeReply} reportComment={reportReplyMutate}>{index === renderReplies.length - 1 && canLoadMore && <ShowMoreReplies loadMore={loadMore} />}</CommentBlock>)}</div></>}{(isLoading || isValidating) && <LoadingDots size="md" />}</div>;
    $[73] = addReply;
    $[74] = canLoadMore;
    $[75] = comment.reply_count;
    $[76] = isLoading;
    $[77] = isValidating;
    $[78] = loadMore;
    $[79] = removeReply;
    $[80] = renderReplies;
    $[81] = reportReplyMutate;
    $[82] = showReplies;
    $[83] = updateReplyLikeCount;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  let t24;
  if ($[85] !== lastCommentRef || $[86] !== t22 || $[87] !== t23) {
    t24 = <div data-component="comment-item" className="flex h-fit w-full flex-col gap-2" ref={lastCommentRef}>{t22}{t23}</div>;
    $[85] = lastCommentRef;
    $[86] = t22;
    $[87] = t23;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  return t24;
}
function _temp5(prev_4) {
  return prev_4 + 1;
}
function _temp4(prev_3) {
  return !prev_3;
}
function _temp3(reply_2) {
  return reply_2.id;
}
function _temp2(reply) {
  return reply.id;
}
function _temp(page) {
  return page?.results || [];
}
export default Comment;